import {
  Banknote, CircleUser, ClipboardCheck, EyeOff, FileSignature, FileText, UserPlus, type LucideIcon,
} from 'lucide-react';
import {
  DEFAULT_ESPACE_OPTIONS, DEFAULT_MODULES, MODULE_KEYS,
  type EspaceOptions, type ModuleFlags, type ModuleKey,
} from '@pointage/shared';
import { toast } from '../../store/toast.js';
import { useSettings, useUpdateSettings, type SettingsMap } from '../../api/hooks.js';
import { RecSettingsPanel } from './RecSettingsPanel.js';

// Modules & visibilité — switch whole features on/off app-wide.
// Off = pages masquées pour tout le monde + API verrouillée (403)
// côté serveur. Le cœur pointage (saisie/grille/employés) n'est pas
// désactivable.

interface ModuleDef { key: ModuleKey; Icon: LucideIcon; title: string; desc: string; }
const MODULES: ModuleDef[] = [
  { key: 'payroll',     Icon: Banknote,      title: 'Salaires & bonus',
    desc: 'Table mensuelle, arbitrage des bonus et fichier de virement bancaire.' },
  { key: 'evaluations', Icon: ClipboardCheck, title: 'Évaluations annuelles',
    desc: 'Campagnes d’auto-évaluation, entretiens et objectifs.' },
  { key: 'contrats',    Icon: FileSignature,  title: 'Période d’essai & CDD',
    desc: 'Jalons d’essai, renouvellements et échéances de CDD.' },
  { key: 'documents',   Icon: FileText,       title: 'Demandes de documents',
    desc: 'File RH des attestations et documents demandés par les employés.' },
  { key: 'espace',      Icon: CircleUser,     title: 'Espace employé',
    desc: 'Portail self-service : soldes, demandes, attestation, évaluation.' },
  { key: 'recrutement', Icon: UserPlus,       title: 'Recrutement & onboarding',
    desc: 'Demandes, pipeline candidats, offres validées et onboarding.' },
];

const ESPACE_OPTS: Array<{ key: keyof EspaceOptions; label: string; desc: string }> = [
  { key: 'leaveRequests', label: 'Demandes de congé',    desc: 'L’employé peut soumettre une demande de congé/récup.' },
  { key: 'recupClaims',   label: 'Récup week-end/férié', desc: 'Déclaration de jours travaillés à récupérer.' },
  { key: 'docRequests',   label: 'Demandes de documents', desc: 'Attestation, fiches de paie… via la file RH.' },
  { key: 'attestation',   label: 'Attestation instantanée', desc: 'Téléchargement direct de l’attestation de travail PDF.' },
];

interface Props { ping: () => void; }

export function ModulesCard({ ping }: Props) {
  const settings = useSettings();
  const update = useUpdateSettings();
  const map: SettingsMap = settings.data ?? {};

  const stored = (map.modules ?? {}) as Partial<Record<ModuleKey, unknown>>;
  const flags: ModuleFlags = Object.fromEntries(
    MODULE_KEYS.map((k) => [k, stored[k] !== false]),
  ) as unknown as ModuleFlags;
  const storedOpts = (map.espaceOptions ?? {}) as Partial<Record<keyof EspaceOptions, unknown>>;
  const opts: EspaceOptions = {
    attestation: storedOpts.attestation !== false,
    recupClaims: storedOpts.recupClaims !== false,
    docRequests: storedOpts.docRequests !== false,
    leaveRequests: storedOpts.leaveRequests !== false,
  };

  const save = (key: 'modules' | 'espaceOptions', value: unknown) => {
    update.mutate({ [key]: value }, { onSuccess: ping, onError: () => toast.danger('Échec de l’enregistrement.') });
  };
  const toggleModule = (k: ModuleKey) => save('modules', { ...flags, [k]: !flags[k] });
  const toggleOpt = (k: keyof EspaceOptions) => save('espaceOptions', { ...opts, [k]: !opts[k] });

  if (!settings.data) {
    return <div className="scard-body"><p className="srow-help" style={{ padding: 16 }}>Chargement…</p></div>;
  }

  return (
    <div className="scard-body">
      <div className="mod-grid">
        {MODULES.map(({ key, Icon, title, desc }) => {
          const on = flags[key];
          return (
            <div key={key} className={'modcard' + (on ? ' on' : '')} data-testid={`mod-${key}`}>
              <div className="modcard-top">
                <div className="modcard-ico"><Icon aria-hidden="true" /></div>
                <div className="modcard-titles">
                  <div className="modcard-title">{title}</div>
                  <div className="modcard-desc">{desc}</div>
                </div>
                <button className={'toggle' + (on ? ' on' : '')} role="switch" aria-checked={on}
                  aria-label={title} data-testid={`mod-toggle-${key}`} onClick={() => toggleModule(key)} />
              </div>
              {!on && (
                <div className="modcard-off">
                  <EyeOff aria-hidden="true" />
                  Masqué pour tous — pages retirées de la navigation, API verrouillée.
                  {key === 'espace' && ' La connexion des comptes employés est suspendue.'}
                </div>
              )}
              {key === 'espace' && on && (
                <div className="mod-opts" data-testid="mod-espace-opts">
                  {ESPACE_OPTS.map((o) => (
                    <div key={o.key} className="mod-opt">
                      <div>
                        <div className="mod-opt-label">{o.label}</div>
                        <div className="mod-opt-desc">{o.desc}</div>
                      </div>
                      <button className={'toggle sm' + (opts[o.key] ? ' on' : '')} role="switch"
                        aria-checked={opts[o.key]} aria-label={o.label}
                        data-testid={`mod-opt-${o.key}`} onClick={() => toggleOpt(o.key)} />
                    </div>
                  ))}
                </div>
              )}
              {key === 'recrutement' && on && <RecSettingsPanel ping={ping} />}
            </div>
          );
        })}
      </div>
      <p className="srow-help" style={{ padding: '10px 6px 0' }}>
        Le cœur pointage — tableau de bord, saisie, grille, employés, congés, rapports — reste toujours actif.
        Ces réglages valent pour tous les comptes ; pour restreindre <i>une personne</i>, passez par Accès &amp; utilisateurs.
      </p>
    </div>
  );
}

export { DEFAULT_MODULES, DEFAULT_ESPACE_OPTIONS };
